import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeIconButton } from '@/components/ui/tee-icon-button';
import { WhyGopherNote } from './WhyGopherNote';

export interface AssistantMobileMenuProps {
  onNewConversation: () => void;
  onHistory: () => void;
  onTogglePin: () => void;
  /** Row text for pin/unpin; omitted when there is nothing to pin. */
  pinLabel?: string;
  onEscalate: () => void;
}

/** Mobile "More" menu (UI-SPEC M4). */
export function AssistantMobileMenu({
  onNewConversation,
  onHistory,
  onTogglePin,
  pinLabel,
  onEscalate,
}: AssistantMobileMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <TeeIconButton
          className="h-11 w-11"
          aria-label={t('assistant.panel.more')}
          icon={<MoreHorizontal size={20} />}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[240px] rounded-[10px] border-0 bg-[var(--background-surface-floating)] p-1 shadow-[var(--shadow-popover)]"
      >
        <TeeButton
          variant="ghost"
          size="m"
          className="min-h-[44px] w-full justify-start"
          onClick={run(onNewConversation)}
          text={t('assistant.panel.newConversation')}
        />
        <TeeButton
          variant="ghost"
          size="m"
          className="min-h-[44px] w-full justify-start"
          onClick={run(onHistory)}
          text={t('assistant.panel.history')}
        />
        {pinLabel && (
          <TeeButton
            variant="ghost"
            size="m"
            className="min-h-[44px] w-full justify-start"
            onClick={run(onTogglePin)}
            text={pinLabel}
          />
        )}
        <div className="my-1 h-px bg-[var(--border-default)]" />
        <TeeButton
          variant="ghost"
          size="m"
          className="min-h-[44px] w-full justify-start font-semibold text-[var(--color-forest)]"
          onClick={run(onEscalate)}
          text={t('assistant.composer.escalate')}
        />
        <WhyGopherNote />
      </PopoverContent>
    </Popover>
  );
}
